import { cn } from '@/lib/utils'
import type { FacilityDetail } from '@/types/facility'
import { FACILITY_TYPE_LABELS } from '@/types/common'
import { MapPin, Star, Users, Heart, Calculator, Phone, Award, FileCheck } from 'lucide-react'
import { EVALUATION_SOURCE_ORG, buildEvaluationBasisLabel } from '@/lib/facility/evaluationLabel'

interface ComparisonTableProps {
  facilities: FacilityDetail[]
  className?: string
}

const GRADE_ORDER: Record<string, number> = { A: 5, B: 4, C: 3, D: 2, E: 1 }
const GRADE_COLORS: Record<string, string> = {
  A: 'bg-green-100 text-green-700 border-green-200',
  B: 'bg-blue-100 text-blue-700 border-blue-200',
  C: 'bg-amber-100 text-amber-700 border-amber-200',
  D: 'bg-orange-100 text-orange-700 border-orange-200',
  E: 'bg-red-100 text-red-700 border-red-200',
}

interface ComparisonRow {
  icon: typeof Star
  label: string
  values: string[]
  bestIndices: number[]
}

function buildRows(facilities: FacilityDetail[]): ComparisonRow[] {
  const rows: ComparisonRow[] = []

  // 평가등급
  const grades = facilities.map((f) => f.evaluationGrade ?? '-')
  const gradeScores = grades.map((g) => GRADE_ORDER[g] ?? 0)
  const maxGrade = Math.max(...gradeScores)
  rows.push({
    icon: Award,
    label: '평가등급',
    values: grades.map((g) => (g === '-' ? '-' : `${g}등급`)),
    bestIndices: gradeScores.map((s, i) => (s === maxGrade && s > 0 ? i : -1)).filter((i) => i >= 0),
  })

  // 평가점수
  const scores = facilities.map((f) => f.evaluationScore)
  const validScores = scores.map((s) => s ?? 0)
  const maxScore = Math.max(...validScores)
  rows.push({
    icon: Star,
    label: '평가점수',
    values: scores.map((s) => (s != null ? `${s}점` : '정보 없음')),
    bestIndices: validScores.map((s, i) => (s === maxScore && s > 0 ? i : -1)).filter((i) => i >= 0),
  })

  // 평가 기준 — 평가는 (기관기호 × 급여종류) 단위라 어느 급여의 평가인지 없으면 등급 비교가 어긋난다.
  // 기관명(국민건강보험공단)은 표 하단 각주에 한 번만 표기.
  // 시설 유형과 다른 급여종류 평가를 빌려 온 시설은 `… 급여 기준` 으로 구분한다(§2.5).
  // (facility-detail-evaluation-spec.md §2, §2.5, §3)
  rows.push({
    icon: FileCheck,
    label: '평가 기준',
    values: facilities.map(
      (f) =>
        buildEvaluationBasisLabel(f.evaluationCategory, f.evaluationServiceKind, f.facilityType) || '정보 없음',
    ),
    bestIndices: [],
  })

  // 시설유형
  rows.push({
    icon: MapPin,
    label: '시설유형',
    values: facilities.map((f) => FACILITY_TYPE_LABELS[f.facilityType as keyof typeof FACILITY_TYPE_LABELS] ?? f.facilityType),
    bestIndices: [],
  })

  // 잔여정원
  const vacancies = facilities.map((f) => Math.max(f.capacity - f.currentCount, 0))
  const maxVacancy = Math.max(...vacancies)
  rows.push({
    icon: Users,
    label: '잔여정원',
    values: vacancies.map((v) => `${v}명`),
    bestIndices: vacancies.map((v, i) => (v === maxVacancy && v > 0 ? i : -1)).filter((i) => i >= 0),
  })

  // 치매전담
  rows.push({
    icon: Heart,
    label: '치매전담',
    values: facilities.map((f) => (f.hasDementiaCare ? '운영' : '-')),
    bestIndices: facilities.map((f, i) => (f.hasDementiaCare ? i : -1)).filter((i) => i >= 0),
  })

  // 비급여(월)
  const costs = facilities.map((f) => {
    if (!f.nonCoveredCosts || f.nonCoveredCosts.length === 0) return null
    return f.nonCoveredCosts.reduce((sum, c) => sum + c.amount * 30, 0)
  })
  const validCosts = costs.filter((c): c is number => c !== null)
  const minCost = validCosts.length > 0 ? Math.min(...validCosts) : 0
  rows.push({
    icon: Calculator,
    label: '비급여(월)',
    values: costs.map((c) => c != null ? `약 ${Math.round(c / 10000)}만원` : '정보 없음'),
    bestIndices: costs.map((c, i) => (c === minCost && c !== null ? i : -1)).filter((i) => i >= 0),
  })

  // 전화번호
  rows.push({
    icon: Phone,
    label: '전화번호',
    values: facilities.map((f) => f.phone ?? '정보 없음'),
    bestIndices: [],
  })

  return rows
}

export default function ComparisonTable({ facilities, className }: ComparisonTableProps) {
  const rows = buildRows(facilities)

  // 컬럼 템플릿: 첫 열(항목명) 고정 + 시설 열은 minmax(0,1fr)로 콘텐츠와 무관하게 균등 분할
  // → 헤더행/본문행이 별도 grid여도 모든 행의 컬럼 폭이 동일하게 고정된다(정렬 어긋남 해결).
  const gridTemplate = `136px repeat(${facilities.length}, minmax(0, 1fr))`
  // 모바일에서 시설 열이 좁아 잘리지 않도록 최소 폭을 확보 → 넘치면 가로 스크롤
  const minWidth = 136 + facilities.length * 150

  return (
    <div className={cn('', className)}>
      {/* 통합 테이블 (카드 헤더 + 비교 항목을 같은 그리드로). 좁은 화면에선 가로 스크롤 */}
      <div className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-x-auto">
        <div style={{ minWidth: `${minWidth}px` }}>
          {/* 시설명 헤더 행 */}
          <div className="grid items-stretch border-b border-gray-100" style={{ gridTemplateColumns: gridTemplate }}>
            {/* 좌측 항목명 열은 sticky로 가로 스크롤 시에도 고정 */}
            <div className="sticky left-0 z-10 bg-gray-50" />
            {facilities.map((f) => {
              const grade = f.evaluationGrade
              const gradeStyle = grade ? GRADE_COLORS[grade] ?? 'bg-gray-100 text-gray-600' : ''
              return (
                <div key={f.id} className="p-4 text-center border-l border-gray-50">
                  {grade && (
                    <span className={`inline-block rounded-full border px-3 py-1 text-xs font-bold mb-2 ${gradeStyle}`}>
                      {grade}등급
                    </span>
                  )}
                  <h3 className="text-sm font-bold text-gray-900 break-keep line-clamp-2 leading-snug">{f.name}</h3>
                  <p className="mt-1 text-xs text-gray-400 truncate flex items-center justify-center gap-1">
                    <MapPin size={10} className="flex-shrink-0" />
                    <span className="truncate">{f.addressSido} {f.addressSigungu}</span>
                  </p>
                </div>
              )
            })}
          </div>

          {/* 비교 항목 행 */}
          {rows.map((row, rowIdx) => (
            <div
              key={row.label}
              className={`grid items-center ${rowIdx > 0 ? 'border-t border-gray-50' : ''}`}
              style={{ gridTemplateColumns: gridTemplate }}
            >
              <div className="sticky left-0 z-10 flex items-center gap-1.5 px-4 py-4 bg-gray-50">
                <row.icon size={14} className="text-gray-400 flex-shrink-0" />
                <span className="text-sm font-semibold text-gray-600 whitespace-nowrap">{row.label}</span>
              </div>
              {row.values.map((value, colIdx) => {
                const isBest = row.bestIndices.includes(colIdx)
                return (
                  <div
                    key={colIdx}
                    className={cn(
                      'px-3 py-4 text-center text-sm border-l border-gray-50 break-keep',
                      isBest ? 'bg-blue-50/70 font-bold text-blue-600' : 'text-gray-700'
                    )}
                  >
                    {value}
                    {isBest && <span className="ml-1 text-[10px] text-blue-400">Best</span>}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
      {/* 평가 출처 각주 — 평가등급·평가점수·평가 기준 행의 출처 (심평원 아님) */}
      <p className="mt-2 px-1 text-[11px] text-gray-400">
        평가등급·평가점수 출처: {EVALUATION_SOURCE_ORG} 장기요양기관 평가 결과. 평가는 급여종류별로 산정됩니다.
      </p>
    </div>
  )
}
